import client from './client';

/* Porting del vecchio `api.js`. Cosa è cambiato e perché:
 *
 * - via l'antipattern `new Promise` che avvolgeva una Promise (7 occorrenze).
 *   In tre casi chiamava `reject(resp)` e *proseguiva* con `resolve(...)`,
 *   rendendo la reject inefficace; in altri tre non gestiva affatto l'errore,
 *   quindi su rete caduta la Promise restava pending per sempre e il
 *   componente si bloccava in uno stato indefinito senza mai fallire;
 * - gli URL erano costruiti per concatenazione non escapata
 *   (`?search=${text}`): uno username o una ricerca con `&` o `#` rompeva la
 *   query. Ora tutto passa da `params`, che axios serializza correttamente;
 * - rimossi `Pin.createFromURL` / `Pin.createFromUploaded` (alias identici a
 *   `create`) e `Board.fetchSiteFullList` (mai chiamata);
 * - il filtro per board passa da `?pins__id=` a `?boards__id=` (related_name
 *   corretto lato backend);
 * - il logout è una POST e non più una GET.
 */

export const PINS_PER_PAGE = 30;
export const BOARDS_PER_PAGE = 50;

export const Pin = {
  list({ offset = 0, limit = PINS_PER_PAGE, tag, username, boardId, search, ordering = '-id' } = {}) {
    const params = { limit, offset, ordering };
    if (tag) params.tags__name = tag;
    if (username) params.submitter__username = username;
    if (boardId) params.boards__id = boardId;
    if (search) params.search = search;
    return client.get('pins/', { params }).then((r) => r.data);
  },

  /** Un singolo pin, nella stessa forma di una pagina di lista. */
  async one(pinId) {
    const { data } = await client.get(`pins/${pinId}/`);
    return { results: [data], next: null, count: 1 };
  },

  create(payload) {
    return client.post('pins/', payload).then((r) => r.data);
  },

  update(pinId, payload) {
    return client.patch(`pins/${pinId}/`, payload).then((r) => r.data);
  },

  remove(pinId) {
    return client.delete(`pins/${pinId}/`);
  },

  uploadImage(file) {
    const form = new FormData();
    form.append('image', file);
    return client
      .post('images/', form, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((r) => r.data);
  },
};

export const Board = {
  /* L'ordine non si chiede: l'API serve le board già alfabetiche. Non lo si
   * rifà nemmeno qui, perché la lista è paginata e riordinare pagina per
   * pagina darebbe trenta board in ordine, poi altre trenta che ricominciano
   * dalla A.
   * `ordering: 'name'` era esplicito e andava tolto: è un ORDER BY sensibile
   * alle maiuscole, e spezzava la griglia in un blocco maiuscolo (3D, ATOMIC,
   * Architecture…) seguito da uno minuscolo (arabic font, bath…). */
  list({ username, search, offset = 0, limit = BOARDS_PER_PAGE, ordering } = {}) {
    const params = { limit, offset };
    if (ordering) params.ordering = ordering;
    if (username) params.submitter__username = username;
    if (search) params.search = search;
    return client.get('boards/', { params }).then((r) => r.data);
  },

  one(boardId) {
    return client.get(`boards/${boardId}/`).then((r) => r.data);
  },

  /** Elenco completo e non paginato, per i selettori. */
  autocomplete(username) {
    const params = username ? { submitter__username: username } : {};
    return client.get('boards-auto-complete/', { params }).then((r) => r.data);
  },

  create(name, isPrivate = false) {
    return client.post('boards/', { name, private: isPrivate }).then((r) => r.data);
  },

  update(boardId, payload) {
    return client.patch(`boards/${boardId}/`, payload).then((r) => r.data);
  },

  remove(boardId) {
    return client.delete(`boards/${boardId}/`);
  },

  /* L'API accetta al massimo 10 id per chiamata: i chiamanti che ne hanno di
   * più devono spezzare la richiesta (vedi `addPins`). */
  addPins(boardId, pinIds) {
    const batches = [];
    for (let i = 0; i < pinIds.length; i += 10) {
      batches.push(client.patch(`boards/${boardId}/`, { pins_to_add: pinIds.slice(i, i + 10) }));
    }
    return Promise.all(batches);
  },

  removePins(boardId, pinIds) {
    return client.patch(`boards/${boardId}/`, { pins_to_remove: pinIds });
  },

  /* Import in blocco (il drop di una cartella dal Finder): le immagini
   * finiscono nella board indicata per id, oppure per nome — creata se manca.
   * L'endpoint accetta al massimo 50 file per chiamata; chi ne ha di più
   * spezza (vedi useImportCartella, che viaggia a lotti ben più piccoli). */
  importFolder({ boardId = null, name = null, files }) {
    const form = new FormData();
    if (boardId) form.append('board_id', boardId);
    else form.append('board', name);
    for (const file of files) form.append('images', file);
    return client
      .post('boards/import-folder/', form, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((r) => r.data);
  },
};

export const Tag = {
  list() {
    return client.get('tags-auto-complete/').then((r) => r.data);
  },
};

export const User = {
  /** L'utente corrente, o null se non autenticato. */
  async me() {
    const { data } = await client.get('profile/users/');
    return data.length ? data[0] : null;
  },

  /** Profilo pubblico per username, o null se non esiste. */
  async byName(username) {
    const { data } = await client.get('profile/public-users/', { params: { username } });
    return data.length ? data[0] : null;
  },

  logIn(username, password) {
    return client.post('profile/login/', { username, password }).then((r) => r.data);
  },

  signUp({ username, email, password, passwordRepeat }) {
    return client
      .post('profile/users/', {
        username,
        email,
        password,
        password_repeat: passwordRepeat,
      })
      .then((r) => r.data);
  },

  logOut() {
    return client.post('profile/logout/');
  },
};

export default { Pin, Board, Tag, User };
